import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Stack,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import SuperAdminService from '../../services/super-admin.service';
import { CancellationData } from '../../types/super-admin';

interface SubscriptionCancellationsProps {
  open: boolean;
  onClose: () => void;
}

const SubscriptionCancellations: React.FC<SubscriptionCancellationsProps> = ({ open, onClose }) => {
  const [cancellations, setCancellations] = useState<CancellationData[]>([]);
  const [filteredCancellations, setFilteredCancellations] = useState<CancellationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{ total: number; returned: number; limit: number | null; skip: number | null } | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Filters
  const [reasonFilter, setReasonFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [planIdFilter, setPlanIdFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const fetchCancellations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await SuperAdminService.getCancellations();
      
      // Handle nested response structure: response.data is CancellationsResponse
      // response.data.data contains the array of CancellationData
      if (response.data?.success && Array.isArray(response.data.data)) {
        setCancellations(response.data.data);
        setFilteredCancellations(response.data.data);
        setPagination(response.data.pagination || null);
      } else {
        setError('Failed to fetch cancellations data');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching cancellations');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...cancellations];

    // Filter by reason
    if (reasonFilter !== 'all') {
      filtered = filtered.filter(cancellation => 
        cancellation.cancellation_reason === reasonFilter
      );
    }

    // Filter by date range
    if (dateFromFilter) {
      const fromDate = new Date(dateFromFilter);
      filtered = filtered.filter(cancellation => {
        const cancellationDate = new Date(cancellation.createdAt || '');
        return cancellationDate >= fromDate;
      });
    }

    if (dateToFilter) {
      const toDate = new Date(dateToFilter);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(cancellation => {
        const cancellationDate = new Date(cancellation.createdAt || '');
        return cancellationDate <= toDate;
      });
    }

    // Filter by search term (searches email, customer_id, subscription_id, cognito_user_id)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(cancellation => 
        cancellation.email?.toLowerCase().includes(searchLower) ||
        cancellation.customer_id?.toLowerCase().includes(searchLower) ||
        cancellation.subscription_id?.toLowerCase().includes(searchLower) ||
        cancellation.cognito_user_id?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by plan ID
    if (planIdFilter !== 'all') {
      filtered = filtered.filter(cancellation => cancellation.plan_id === planIdFilter);
    }

    // Filter by subscription status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(cancellation => cancellation.subscription_status === statusFilter);
    }

    setFilteredCancellations(filtered);
  };

  const clearFilters = () => {
    setReasonFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setSearchTerm('');
    setPlanIdFilter('all');
    setStatusFilter('all');
    setFilteredCancellations(cancellations);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  /** Convert amount from cents to formatted dollar string (e.g., 204000 -> $2,040.00) */
  const formatAmountFromCents = (cents: number | undefined): string => {
    if (cents == null) return 'N/A';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      return 'N/A';
    }
  };

  /** Convert Unix timestamp to readable date string */
  const formatUnixTimestamp = (timestamp: number | undefined): string => {
    if (timestamp == null) return 'N/A';
    try {
      return new Date(timestamp * 1000).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const getReasonColor = (reason: string) => {
    const reasonLower = reason.toLowerCase();
    if (reasonLower.includes('expensive')) return 'error';
    if (reasonLower.includes('technical')) return 'warning';
    if (reasonLower.includes('performance')) return 'info';
    if (reasonLower.includes('setup')) return 'secondary';
    return 'default';
  };

  const getStatusDisplay = (status: string | undefined) => {
    if (!status) return { label: 'N/A', color: 'default' as const };
    const normalized = status.toLowerCase();
    if (normalized === 'active') return { label: 'Active', color: 'success' as const };
    if (normalized === 'in_trial') return { label: 'In Trial', color: 'info' as const };
    return { label: status, color: 'default' as const };
  };

  const uniqueReasons = Array.from(new Set(cancellations.map(c => c.cancellation_reason).filter(Boolean)));
  const uniquePlans = Array.from(new Set(cancellations.map(c => c.plan_id).filter(Boolean))).sort();
  const uniqueStatuses = Array.from(new Set(cancellations.map(c => c.subscription_status).filter(Boolean))).sort();

  useEffect(() => {
    if (open) {
      fetchCancellations();
    }
  }, [open]);

  useEffect(() => {
    applyFilters();
  }, [reasonFilter, dateFromFilter, dateToFilter, searchTerm, planIdFilter, statusFilter, cancellations]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CancelIcon color="error" />
            <Typography variant="h5" component="div">
              Subscription Cancellations
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <CancelIcon color="error" sx={{ mr: 1 }} />
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Total Cancellations
                        </Typography>
                        <Typography variant="h6">
                          {cancellations.length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <FilterListIcon color="primary" sx={{ mr: 1 }} />
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Filtered Results
                        </Typography>
                        <Typography variant="h6">
                          {filteredCancellations.length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <ScheduleIcon color="info" sx={{ mr: 1 }} />
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          In Trial
                        </Typography>
                        <Typography variant="h6">
                          {cancellations.filter(c => (c.subscription_status || '').toLowerCase() === 'in_trial').length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Active
                        </Typography>
                        <Typography variant="h6">
                          {cancellations.filter(c => (c.subscription_status || '').toLowerCase() === 'active').length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <PersonIcon color="secondary" sx={{ mr: 1 }} />
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          With Feedback
                        </Typography>
                        <Typography variant="h6">
                          {cancellations.filter(c => c.feedback).length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Filters */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <FilterListIcon />
                <Typography variant="h6">Filters</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button onClick={fetchCancellations} size="small" variant="outlined" startIcon={<RefreshIcon />}>
                  Refresh Data
                </Button>
                <Button onClick={clearFilters} size="small" variant="outlined">
                  Clear Filters
                </Button>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Cancellation Reason</InputLabel>
                    <Select
                      value={reasonFilter}
                      label="Cancellation Reason"
                      onChange={(e) => setReasonFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Reasons</MenuItem>
                      {uniqueReasons.map(reason => (
                        <MenuItem key={reason} value={reason}>
                          {reason}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date From (UTC)"
                    type="date"
                    value={dateFromFilter}
                    onChange={(e) => setDateFromFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="All dates in UTC"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date To (UTC)"
                    type="date"
                    value={dateToFilter}
                    onChange={(e) => setDateToFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="All dates in UTC"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Plan ID</InputLabel>
                    <Select
                      value={planIdFilter}
                      label="Plan ID"
                      onChange={(e) => setPlanIdFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Plans</MenuItem>
                      {uniquePlans.map(plan => (
                        <MenuItem key={plan} value={plan}>
                          {plan}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Subscription Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Subscription Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      {uniqueStatuses.map(status => (
                        <MenuItem key={status} value={status}>
                          {getStatusDisplay(status).label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              {/* Search Field */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search by Email, Customer ID, Subscription ID, or Cognito User ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Enter email, customer ID, subscription ID, or cognito user ID to search..."
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Cancellations Table */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">
                Cancellation Records ({filteredCancellations.length})
              </Typography>
              {pagination && (
                <Typography variant="body2" color="textSecondary">
                  Showing {pagination.returned} of {pagination.total} total
                </Typography>
              )}
            </Box>
            
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 50 }}></TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Plan ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Cancellation Reason</TableCell>
                    <TableCell>Feedback</TableCell>
                    <TableCell>
                      Date
                      <Typography variant="caption" display="block" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                        (UTC)
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCancellations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No cancellations found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCancellations.map((cancellation, index) => {
                      const isExpanded = expandedRows.has(cancellation.id);
                      const feedbackMaxLength = 100;
                      const truncatedFeedback = cancellation.feedback && cancellation.feedback.length > feedbackMaxLength
                        ? `${cancellation.feedback.substring(0, feedbackMaxLength)}...`
                        : cancellation.feedback;

                      return (
                        <React.Fragment key={cancellation.id || index}>
                          <TableRow 
                            hover 
                            sx={{ cursor: 'pointer' }}
                            onClick={() => toggleRowExpansion(cancellation.id)}
                          >
                            <TableCell padding="checkbox">
                              <IconButton size="small">
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>{cancellation.email || 'N/A'}</TableCell>
                            <TableCell>{formatAmountFromCents(cancellation.current_amount)}</TableCell>
                            <TableCell>{cancellation.plan_id || 'N/A'}</TableCell>
                            <TableCell>
                              {cancellation.subscription_status ? (
                                <Chip
                                  label={getStatusDisplay(cancellation.subscription_status).label}
                                  color={getStatusDisplay(cancellation.subscription_status).color}
                                  size="small"
                                />
                              ) : (
                                'N/A'
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={cancellation.cancellation_reason || 'N/A'}
                                color={getReasonColor(cancellation.cancellation_reason || '') as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {cancellation.feedback ? (
                                <Typography variant="body2" sx={{ 
                                  maxWidth: 200, 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {truncatedFeedback}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  No feedback
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDate(cancellation.createdAt)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell 
                              style={{ paddingBottom: 0, paddingTop: 0 }} 
                              colSpan={8}
                            >
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 2 }}>
                                  <Typography variant="h6" gutterBottom component="div" sx={{ mb: 2 }}>
                                    Full Details
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                      <Stack spacing={1}>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Email
                                          </Typography>
                                          <Typography variant="body2">
                                            {cancellation.email || 'N/A'}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Plan ID
                                          </Typography>
                                          <Typography variant="body2">
                                            {cancellation.plan_id || 'N/A'}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Amount
                                          </Typography>
                                          <Typography variant="body2">
                                            {formatAmountFromCents(cancellation.current_amount)}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Subscription Status
                                          </Typography>
                                          <Box sx={{ mt: 0.5 }}>
                                            <Chip
                                              label={getStatusDisplay(cancellation.subscription_status).label}
                                              color={getStatusDisplay(cancellation.subscription_status).color}
                                              size="small"
                                            />
                                          </Box>
                                        </Box>
                                      </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Stack spacing={1}>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Customer ID
                                          </Typography>
                                          <Typography variant="body2">
                                            {cancellation.customer_id || 'N/A'}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Subscription ID
                                          </Typography>
                                          <Typography variant="body2">
                                            {cancellation.subscription_id || 'N/A'}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Cognito User ID
                                          </Typography>
                                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                            {cancellation.cognito_user_id || 'N/A'}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Started At
                                          </Typography>
                                          <Typography variant="body2">
                                            {formatUnixTimestamp(cancellation.started_at)}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Created At (UTC)
                                          </Typography>
                                          <Typography variant="body2">
                                            {formatDate(cancellation.createdAt)}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Updated At (UTC)
                                          </Typography>
                                          <Typography variant="body2">
                                            {formatDate(cancellation.updatedAt)}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Box>
                                        <Typography variant="caption" color="textSecondary">
                                          Cancellation Reason
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                          <Chip
                                            label={cancellation.cancellation_reason || 'N/A'}
                                            color={getReasonColor(cancellation.cancellation_reason || '') as any}
                                            size="small"
                                          />
                                        </Box>
                                      </Box>
                                    </Grid>
                                    {cancellation.feedback && (
                                      <Grid item xs={12}>
                                        <Box>
                                          <Typography variant="caption" color="textSecondary">
                                            Full Feedback
                                          </Typography>
                                          <Paper 
                                            variant="outlined" 
                                            sx={{ 
                                              p: 2, 
                                              mt: 1,
                                              bgcolor: 'grey.50',
                                              maxHeight: 200,
                                              overflow: 'auto'
                                            }}
                                          >
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                              {cancellation.feedback}
                                            </Typography>
                                          </Paper>
                                        </Box>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionCancellations;

